/**
 * Area `domain-join-offer` of the console gateway — over `apps/api/src/routes/domain-join-offer/` (owner answer 47,
 * 2026-10-06). Owned by the plan-014 integrator.
 *
 *   listDomainJoinOffers    `listDomainJoinOffers`    the signed-in person's offers ("Your colleagues at <org> use
 *                                                     WeTalk — join?"), own identity only
 *   acceptDomainJoinOffer   `acceptDomainJoinOffer`   join with the organization's constrained role; the console then
 *                                                     switches into the organization
 *
 * Domain auto-join is an OFFER: nothing joins a person until they accept. Dismissing one is the console's own
 * (`../../shell/domain-join-offer.tsx`, a per-browser convenience).
 */
import { assertOk, type ApiResult, type WeTalkApiClient } from "@wetalk/api-client";

import type { ConsoleGateway, WorkspaceRoleView } from "../gateway.js";
import { readArray, readRecord, readText } from "../decode.js";
import type { GatewayGap, SliceContext } from "./slice-context.js";

// ── views ─────────────────────────────────────────────────────────────────────────────────────────────────

/** The constrained roles an offer may carry — never Owner or Admin. */
export type DomainJoinRoleView = Exclude<WorkspaceRoleView, "owner" | "admin">;

export interface DomainJoinOfferView {
  /** What `acceptDomainJoinOffer` names (the organization's verified domain). */
  readonly offerId: string;
  readonly accountId: string;
  readonly accountName: string;
  readonly domain: string;
  readonly workspace: readonly { readonly workspaceId: string; readonly name: string; readonly memberRole: DomainJoinRoleView }[];
}

export interface DomainJoinAcceptedView {
  readonly accountId: string;
  /** The first workspace joined, where the console lands; `null`: the organization's own pages. */
  readonly landingWorkspaceId: string | null;
}

declare module "../gateway.js" {
  interface ConsoleGateway {
    /** The signed-in person's offers to join an organization through their verified email domain (possibly none). */
    listDomainJoinOffers(): Promise<readonly DomainJoinOfferView[]>;
    /** Accepts one; answers `domain_join_offer_unavailable` (410) when it no longer stands. */
    acceptDomainJoinOffer(offerId: string): Promise<DomainJoinAcceptedView>;
  }
}

export type DomainJoinOfferGatewaySlice = Pick<ConsoleGateway, "listDomainJoinOffers" | "acceptDomainJoinOffer">;

/** This area's rows of `GATEWAY_GAP`: none — both operations are in the area's fragments. */
export const domainJoinOfferGap: readonly GatewayGap[] = [];

// ── decoding ──────────────────────────────────────────────────────────────────────────────────────────────

const ROLE: readonly DomainJoinRoleView[] = ["member", "viewer", "developer", "campaign_manager", "billing"];

function roleOf(value: unknown, path: string): DomainJoinRoleView {
  const text = readText(value, path);
  const found = ROLE.find((one) => one === text);
  if (found === undefined) throw new Error(`${path}: "${text}" is not a role an offer may carry`);
  return found;
}

export function domainJoinOfferOfWire(value: unknown, path: string): DomainJoinOfferView {
  const row = readRecord(value, path);
  const organization = readRecord(row["organization"], `${path}.organization`);
  return {
    offerId: readText(row["offer_id"], `${path}.offer_id`),
    accountId: readText(organization["account_id"], `${path}.organization.account_id`),
    accountName: readText(organization["name"], `${path}.organization.name`),
    domain: readText(row["domain"], `${path}.domain`),
    workspace: readArray(row["workspace"], `${path}.workspace`).map((one, index) => {
      const at = `${path}.workspace[${index}]`;
      const workspace = readRecord(one, at);
      return {
        workspaceId: readText(workspace["workspace_id"], `${at}.workspace_id`),
        name: readText(workspace["name"], `${at}.name`),
        memberRole: roleOf(workspace["member_role"], `${at}.member_role`),
      };
    }),
  };
}

function dataOf(result: ApiResult, operationId: string): unknown {
  return readRecord(assertOk(result), operationId)["data"];
}

// ── the slice ─────────────────────────────────────────────────────────────────────────────────────────────

export function domainJoinOfferApiSlice(client: WeTalkApiClient, _context: SliceContext): DomainJoinOfferGatewaySlice {
  return {
    async listDomainJoinOffers() {
      const data = dataOf(await client.listDomainJoinOffers(), "listDomainJoinOffers");
      return readArray(data, "listDomainJoinOffers.data").map((one, index) => domainJoinOfferOfWire(one, `listDomainJoinOffers.data[${String(index)}]`));
    },
    async acceptDomainJoinOffer(offerId) {
      const data = readRecord(dataOf(await client.acceptDomainJoinOffer({ path: { offer_id: offerId } }), "acceptDomainJoinOffer"), "acceptDomainJoinOffer.data");
      const landing = readRecord(data["landing"], "acceptDomainJoinOffer.landing");
      const kind = readText(landing["kind"], "acceptDomainJoinOffer.landing.kind");
      return {
        accountId: readText(data["account_id"], "acceptDomainJoinOffer.account_id"),
        landingWorkspaceId: kind === "workspace" ? readText(landing["workspace_id"], "acceptDomainJoinOffer.landing.workspace_id") : null,
      };
    },
  };
}
